import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import { ApiClient } from 'ApiClient';
import { SnackbarContext } from 'components';
import { orderEntryGuard, useOrderEntryGuard } from 'hooks';
import { ExternalOrderLog, BoltParsedPayload } from 'interfaces';
import { ExternalOrderService } from 'services';
import { FirebaseService } from 'services/FirebaseService';
import * as React from 'react';

// Hard cap on how long a Bolt/Wolt order may be held back while a staff
// member is in the manual New Order wizard. Bolt auto-cancels AND pauses
// the venue if an order sits unaccepted ~10 min, so we break through well
// before that: a brief interruption beats a lost order + a paused venue.
const MAX_HOLD_MS = 4 * 60 * 1000; // 4 minutes

// How many times the alert sounds while an order is HELD during manual
// order entry. Enough to be impossible to miss on a busy counter, but it
// stops on its own so staff aren't typing an order against an endless
// siren — the "N orders waiting" chip carries the reminder after that.
// Released / capped alerts go back to looping indefinitely.
const HELD_ALERT_LOOPS = 8;

// Hardcoded user → shops mapping. Each entry is the list of shop_ids
// the alert listens for. Anyone not listed gets no alerts at all
// (silent for engineers, the boss, etc).
//
// Per the user 2026-06-25:
//   pekingrestaurant@... → Peking To Go (shop_id=2) only
//   pekingstreetfood@... → Street Food (shop_id=1) only
//   pekingall@...        → both
const ALERT_SHOPS_BY_EMAIL: Record<string, number[]> = {
  'pekingrestaurant@pekingltd.com': [2],
  'pekingstreetfood@pekingltd.com': [1],
  'pekingall@pekingltd.com': [1, 2],
};

// Website-ordering pilot gate (Stage 1, 2026-09-05): only these logins get
// the takeover for orders from our OWN storefront (pekingmalta.com/order)
// while it is being tested. At cutover (Stage 4) the restaurant logins
// above move in here and this map merges away.
const WEBSITE_ALERT_EMAILS: Record<string, number[]> = {
  'adminpeking@pekingltd.com': [2],
  // Added 2026-09-06: Lucy placed a test order on the restaurant login and
  // got no takeover, because only adminpeking@ was in the pilot. Website
  // orders now take over the restaurant screens the way Bolt and Wolt do.
  // Website ordering is shop 2 only, so pekingall@ gets [2] here even though
  // it watches both shops for the marketplaces.
  'pekingrestaurant@pekingltd.com': [2],
  'pekingall@pekingltd.com': [2],
};

const REJECT_REASONS: Array<{
  value: 'do_not_wish_to_serve_this_client' | 'too_many_orders' | 'other';
  label: string;
}> = [
  { value: 'too_many_orders', label: 'Too many orders' },
  { value: 'do_not_wish_to_serve_this_client', label: 'Cannot serve this client' },
  { value: 'other', label: 'Other' },
];

// A log is awaiting staff action in the popup: a Bolt/Wolt order not yet
// accepted, OR a GloriaFood order held for review because it carries a global
// customer comment (bolt_order_status = 'needs_review', set by the webhook).
const isAwaitingAction = (l: ExternalOrderLog): boolean => {
  const internal = (l.status || '').toLowerCase();
  const bolt = (l.boltOrderStatus || '').toLowerCase();
  if (bolt === 'needs_review') return true;
  return (
    !bolt &&
    (internal === 'received' ||
      internal === 'needs_mapping' ||
      internal === 'parsed')
  );
};

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: 360,
  },
  alertHeader: {
    background: '#1eaf5c',
    color: 'white',
    padding: theme.spacing(2, 3),
    fontSize: '1.5rem',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  pulse: {
    animation: '$pulse 1.2s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.55 },
  },
  customerLine: {
    fontSize: '1.2rem',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  metaChips: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  chipDelivery: {
    background: '#1976d2',
    color: 'white',
    fontWeight: 700,
  },
  chipPickup: {
    background: '#ef6c00',
    color: 'white',
    fontWeight: 700,
  },
  chipScheduled: {
    background: '#0288d1',
    color: 'white',
    fontWeight: 700,
  },
  itemList: {
    background: '#fafafa',
    padding: theme.spacing(1.5, 2),
    borderRadius: 4,
    marginBottom: theme.spacing(2),
    maxHeight: 260,
    overflowY: 'auto',
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 0),
    fontSize: '1rem',
  },
  itemName: {
    fontWeight: 600,
  },
  optionRow: {
    paddingLeft: theme.spacing(2),
    fontSize: '0.88rem',
    color: theme.palette.text.secondary,
  },
  total: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(1),
    borderTop: '1px solid ' + theme.palette.divider,
    fontSize: '1.1rem',
    fontWeight: 700,
  },
  actions: {
    padding: theme.spacing(2, 3),
    display: 'flex',
    gap: theme.spacing(1.5),
  },
  acceptBtn: {
    flex: 2,
    background: '#1eaf5c',
    color: 'white',
    fontSize: '1.05rem',
    fontWeight: 700,
    padding: theme.spacing(1.5),
    '&:hover': { background: '#1a9a52' },
  },
  declineBtn: {
    flex: 1,
    background: theme.palette.error.main,
    color: 'white',
    fontSize: '0.95rem',
    fontWeight: 700,
    padding: theme.spacing(1.5),
    '&:hover': { background: theme.palette.error.dark },
  },
  // Persistent floating button at the top so alert-eligible staff can
  // unlock browser autoplay BEFORE the first order arrives. Once
  // clicked, audio is unlocked for the entire session.
  enableSoundBanner: {
    position: 'fixed',
    top: 12,
    right: 12,
    background: theme.palette.warning.main,
    color: 'white',
    padding: theme.spacing(1, 2),
    borderRadius: 24,
    cursor: 'pointer',
    zIndex: 9999,
    fontWeight: 700,
    fontSize: '0.9rem',
    boxShadow: theme.shadows[6],
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    animation: '$pulseRing 1.6s ease-in-out infinite',
  },
  '@keyframes pulseRing': {
    '0%, 100%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.05)' },
  },
}));

const formatMoney = (
  value: number | null | undefined,
  currency: string | null | undefined,
): string => {
  const n = Number(value ?? 0);
  const c = (currency || 'EUR').toUpperCase();
  const sym = c === 'EUR' ? '€' : c === 'USD' ? '$' : c + ' ';
  return `${sym}${n.toFixed(2)}`;
};

export const NewExternalOrderAlert: React.FC = () => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);

  const [allowedShopIds, setAllowedShopIds] = React.useState<number[] | null>(null);
  // Separate pilot list for our own Website ordering (see WEBSITE_ALERT_EMAILS).
  const [websiteShopIds, setWebsiteShopIds] = React.useState<number[]>([]);
  const [activeAlert, setActiveAlert] = React.useState<ExternalOrderLog | null>(null);

  // Raised while a staff member is in the manual New Order wizard. When
  // true we HOLD the takeover (no modal, no sound) up to MAX_HOLD_MS so
  // order-taking isn't hijacked mid-flow.
  const { orderEntryActive } = useOrderEntryGuard();
  // Re-render tick so the hold auto-expires at the cap even if nothing
  // else changes (only runs while actually holding).
  const [, capTick] = React.useReducer((x: number) => x + 1, 0);
  // How many times the HELD alert has rung, scoped to the order it belongs
  // to, so a new order always gets a fresh budget. A ref (not state) so
  // counting rings never re-renders the popup.
  const heldPlaysRef = React.useRef<{ id: number | null; n: number }>({
    id: null,
    n: 0,
  });

  // Are we currently HOLDING the takeover for this order? Only while the
  // wizard is open AND still within the safety cap. Fail OPEN: if the
  // order has no readable receipt time, show it now rather than risk
  // hiding it past Bolt's accept deadline.
  const receivedMs = activeAlert?.receivedAt
    ? new Date(activeAlert.receivedAt as any).getTime()
    : NaN;
  const heldWithinCap =
    Number.isFinite(receivedMs) && Date.now() - receivedMs < MAX_HOLD_MS;
  const suppressed = !!activeAlert && orderEntryActive && heldWithinCap;

  // While holding, tick every 10s so the cap can expire and the popup
  // break through even if no Firebase bump arrives in the meantime.
  React.useEffect(() => {
    if (!suppressed) return undefined;
    const t = setInterval(() => capTick(), 10000);
    return () => clearInterval(t);
  }, [suppressed]);

  // Report the held count to the New Order wizard (drives its "order
  // waiting" chip). Cleared on unmount so a stale chip can't linger.
  React.useEffect(() => {
    orderEntryGuard.setHeldCount(suppressed ? 1 : 0);
  }, [suppressed]);
  React.useEffect(() => () => orderEntryGuard.setHeldCount(0), []);
  const [acceptOpen, setAcceptOpen] = React.useState(false);
  const [declineOpen, setDeclineOpen] = React.useState(false);
  const [prepMinutes, setPrepMinutes] = React.useState(25);
  // Staff-typed per-item notes, keyed by item index. Held in a REF, not
  // state, so typing causes ZERO re-renders — otherwise every keystroke
  // re-rendered the popup and it churned/re-rang (2026-07-18). Read on
  // accept, cleared on close. Inputs are uncontrolled, keyed by order id.
  const staffItemNotesRef = React.useRef<{ [k: number]: string }>({});
  const [rejectReason, setRejectReason] = React.useState<
    'do_not_wish_to_serve_this_client' | 'too_many_orders' | 'other'
  >('too_many_orders');
  const [rejectComment, setRejectComment] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  // Default to "blocked" so the enable button always shows on a fresh
  // session — iOS Safari blocks unmuted audio autoplay until explicit
  // user gesture, and a muted-audio probe gives false negatives there.
  // sessionStorage flag persists the unlocked state across SPA route
  // changes within the same tab.
  const [audioBlocked, setAudioBlocked] = React.useState<boolean>(() => {
    try {
      return sessionStorage.getItem('alertAudioUnlocked') !== '1';
    } catch (e) {
      return true;
    }
  });

  // Track which logs we've already alerted on this session — prevents
  // re-firing the popup for the same order if the user dismisses then
  // Firebase bumps again before they act.
  const alertedRef = React.useRef<Set<number>>(new Set());
  const audioRef = React.useRef<HTMLAudioElement>(null);

  // 1. Boot: figure out which shops THIS user should be alerted for.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await ApiClient.get<{ emailAddress: string }>(
          'users/with-token',
        );
        if (cancelled) return;
        const email = (me?.emailAddress || '').toLowerCase();
        const shops = ALERT_SHOPS_BY_EMAIL[email] || [];
        setAllowedShopIds(shops);
        setWebsiteShopIds(WEBSITE_ALERT_EMAILS[email] || []);
      } catch (e) {
        if (!cancelled) {
          setAllowedShopIds([]);
          setWebsiteShopIds([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 2. For each allowed shop, listen on Firebase for bumps. Bumps
  //    trigger a silent fetch of that shop's logs. If a fresh
  //    "needs accept" log appears (we've never alerted on it before),
  //    open the popup.
  React.useEffect(() => {
    if (allowedShopIds === null) return;
    const marketplaceShops = allowedShopIds;
    // Union: a pilot login may get Website alerts without marketplace ones.
    const allShops = Array.from(new Set([...marketplaceShops, ...websiteShopIds]));
    if (allShops.length === 0) return;

    const cleanups: Array<() => void> = [];

    allShops.forEach((shopId) => {
      // Subscribe to both Bolt and Wolt paths for this shop. Wolt
      // path stays empty until Bogdan's creds arrive — listener is
      // ready for the moment Wolt webhooks start flowing. The 'website'
      // path (our own storefront) is pilot-gated separately.
      const platforms = [
        ...(marketplaceShops.includes(shopId) ? ['bolt', 'wolt', 'gloriafood'] : []),
        ...(websiteShopIds.includes(shopId) ? ['website'] : []),
      ];
      platforms.forEach((platform) => {
        const path = `external_orders/${platform}/${shopId}`;
        const ref = FirebaseService.getDatabase().ref(path);
        const platformName =
          platform === 'bolt'
            ? 'Bolt'
            : platform === 'wolt'
            ? 'Wolt'
            : platform === 'website'
            ? 'Website'
            : 'GloriaFood';
        const handler = async () => {
          try {
            const logs = await ExternalOrderService.getLogs({
              shopId,
              platform: platformName,
            });
            // First log awaiting staff action we haven't alerted on yet
            // (Bolt/Wolt pending accept, or a GloriaFood held-for-review).
            const pending = logs.find(
              (l) => isAwaitingAction(l) && !alertedRef.current.has(l.id),
            );
            // One atomic transition off the TRUE current value, so we
            // never clobber an order that's still showing/held with a
            // newer one (a lost Bolt order = missed accept deadline):
            //   1. Auto-dismiss the current alert if its order is no longer
            //      ours — accepted on another device (multi-device sync,
            //      2026-07-15) or cancelled by Bolt/the customer while
            //      ringing (2026-07-16). Clearing it also stops the audio.
            //   2. Only once nothing valid is showing do we promote a fresh
            //      pending order. A pending order that loses this race stays
            //      un-alerted and promotes on a later tick once the current
            //      one clears (its own accept/cancel bump drives that).
            setActiveAlert((current) => {
              let kept = current;
              if (current) {
                // Only the fetch for THIS alert's own platform can speak to
                // it — a Wolt fetch must not close a ringing Bolt alert just
                // because the Bolt order isn't in Wolt's list.
                const currentPlatform = (
                  current.externalOrderSource?.name || 'Bolt'
                ).toLowerCase();
                if (currentPlatform === platform) {
                  const mine = logs.find((l) => l.id === current.id);
                  if (!mine) {
                    kept = null; // gone from its own platform's list
                  } else {
                    const internal = (mine.status || '').toLowerCase();
                    const bolt = (mine.boltOrderStatus || '').toLowerCase();
                    const cancelled =
                      bolt === 'cancelled' ||
                      internal === 'cancelled' ||
                      internal === 'cancel_received';
                    kept = !cancelled && isAwaitingAction(mine) ? current : null;
                  }
                }
              }
              if (!kept && pending) {
                alertedRef.current.add(pending.id);
                return pending;
              }
              return kept;
            });
          } catch (e) {
            // Silent — alert is best-effort. The External Orders
            // screen polling is the safety net.
          }
        };
        // Error callback so any future Firebase permission/connectivity
        // regression surfaces immediately (kept after the initial
        // 2026-06-25 PERMISSION_DENIED debug).
        ref.on(
          'value',
          handler,
          (err: Error) => {
            // eslint-disable-next-line no-console
            console.error('Firebase listener error on', path, err);
          },
        );
        cleanups.push(() => ref.off('value', handler));
      });
    });

    return () => {
      cleanups.forEach((c) => c());
    };
  }, [allowedShopIds, websiteShopIds]);

  // 3. Audio. Released alert = loop until handled. HELD alert (staff are
  //    mid manual order) = still ring, HELD_ALERT_LOOPS times, then fall
  //    silent and let the "orders waiting" chip carry the reminder. The
  //    full looping alarm resumes the instant the hold is released or the
  //    safety cap fires.
  React.useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;

    const start = () => {
      // Only (re)start if not already ringing. Without this guard any
      // re-render while the popup is open — e.g. typing a per-item note —
      // resets currentTime and the loop audibly restarts (2026-07-18).
      if (!el.paused) return;
      el.currentTime = 0;
      el.play()
        .then(() => setAudioBlocked(false))
        .catch(() => {
          // Still blocked despite our earlier probe — keep button.
          setAudioBlocked(true);
        });
    };

    if (!activeAlert) {
      el.pause();
      el.currentTime = 0;
      heldPlaysRef.current = { id: null, n: 0 };
      return undefined;
    }

    // Restart the held-ring budget whenever a different order takes over.
    if (heldPlaysRef.current.id !== activeAlert.id) {
      heldPlaysRef.current = { id: activeAlert.id, n: 0 };
    }

    if (!suppressed) {
      el.loop = true;
      start();
      return undefined;
    }

    // Held: ring a bounded number of times using the 'ended' event, since
    // <audio loop> is all-or-nothing.
    el.loop = false;
    const onEnded = () => {
      heldPlaysRef.current.n += 1;
      if (heldPlaysRef.current.n < HELD_ALERT_LOOPS) {
        el.currentTime = 0;
        el.play().catch(() => undefined);
      }
    };
    el.addEventListener('ended', onEnded);
    if (heldPlaysRef.current.n < HELD_ALERT_LOOPS) start();
    return () => el.removeEventListener('ended', onEnded);
  }, [activeAlert, suppressed]);

  const enableAudioManually = () => {
    const el = audioRef.current;
    if (!el) return;
    // Mute so we don't blast the alert when they click. Goal: unlock
    // autoplay rights for the session, not actually play. Real alerts
    // fire later via the activeAlert effect with audio unmuted.
    el.muted = true;
    el.play()
      .then(() => {
        el.pause();
        el.currentTime = 0;
        el.muted = false;
        setAudioBlocked(false);
        try {
          sessionStorage.setItem('alertAudioUnlocked', '1');
        } catch (e) {
          // private mode etc — just continue, button hides anyway
        }
      })
      .catch(() => {
        el.muted = false;
      });
  };

  // Detect platform from the log's source, default to 'Bolt' (legacy).
  // Wolt orders carry externalOrderSource.name === 'Wolt' from the API.
  const platform: 'Bolt' | 'Wolt' | 'GloriaFood' | 'Website' = React.useMemo(() => {
    const name = activeAlert?.externalOrderSource?.name;
    if (name === 'Wolt') return 'Wolt';
    if (name === 'GloriaFood') return 'GloriaFood';
    if (name === 'Website') return 'Website';
    return 'Bolt';
  }, [activeAlert]);
  // GloriaFood orders are already accepted on GloriaFood's side, so this popup
  // is a REVIEW step, not an accept/decline gate: staff distribute the global
  // comment onto the dishes as kitchen notes, then push. No decline, no prep.
  const isGloria = platform === 'GloriaFood';
  // Website = our own storefront: full accept/decline gate like Bolt, and the
  // accept quotes the waiting time the CUSTOMER sees on their status page.
  const isWebsite = platform === 'Website';

  // Platform accent: Wolt blue, Bolt green, GloriaFood orange, Website purple —
  // so staff know the source at a glance and grab the right bag/label.
  const accent =
    platform === 'Wolt'
      ? '#1976d2'
      : isGloria
      ? '#e65100'
      : isWebsite
      ? '#6a1b9a'
      : '#1eaf5c';

  const handleAccept = async () => {
    if (!activeAlert) return;
    try {
      setSubmitting(true);
      // Staff notes typed per item. Sent as an array of {index, note}
      // (the form-data serialiser preserves arrays-of-objects). They
      // become real per-item notes on the chef screen.
      const notesMap = staffItemNotesRef.current;
      const itemNotes = Object.keys(notesMap)
        .map((k) => ({ index: Number(k), note: (notesMap[Number(k)] || '').trim() }))
        .filter((n) => n.note.length > 0);
      if (platform === 'GloriaFood') {
        // Already accepted on GloriaFood's side — just fire it with the
        // reviewed per-item notes. Whether it schedules (for_later) or fires
        // now is decided server-side, so there's no prep time to send.
        await ExternalOrderService.gloriafoodPush(activeAlert.id, { itemNotes });
      } else if (platform === 'Website') {
        // Our own storefront: one call marks accepted, fires the kitchen
        // (or schedules a pre-order) and sets the ETA the customer sees.
        await ExternalOrderService.websiteAccept(activeAlert.id, {
          prepMinutes,
          itemNotes,
        });
      } else if (platform === 'Wolt') {
        // Wolt expects an ISO-8601 adjusted pickup time, not minutes.
        const pickupAt = new Date(Date.now() + prepMinutes * 60 * 1000).toISOString();
        // SCHEDULED (pre-)orders must be ACKED with confirm-preorder.
        // Calling accept ahead of the preorder time is refused with
        // 400 "Cannot move scheduled order to production ... adjusted
        // pickup time is less than preorder time" (2026-07-20). Our
        // controller treats confirm-preorder as an accept internally, so
        // the kitchen bridge and staff notes still run.
        const parsedNow: any = activeAlert.parsedPayload || {};
        const isPreorder =
          (parsedNow.fulfilmentTiming || parsedNow.fulfilment_timing) === 'preorder';
        if (isPreorder) {
          await ExternalOrderService.woltConfirmPreorder(activeAlert.id, {
            adjustedPickupTime: pickupAt,
            itemNotes,
          });
        } else {
          await ExternalOrderService.woltAccept(activeAlert.id, {
            adjustedPickupTime: pickupAt,
            itemNotes,
          });
        }
      } else {
        await ExternalOrderService.boltAccept(activeAlert.id, {
          delayTimeInSeconds: prepMinutes * 60,
          itemNotes,
        });
      }
      snackbar.open(
        isGloria ? 'Pushed to kitchen' : `Accepted in ${prepMinutes} min`,
      );
      closeAll();
    } catch (e) {
      snackbar.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!activeAlert) return;
    try {
      setSubmitting(true);
      if (platform === 'Website') {
        // Our own storefront: free-form reason, shown to nobody but kept on
        // the log for the record. The customer's status page flips to
        // "rejected" and tells them to call us.
        await ExternalOrderService.websiteReject(activeAlert.id, {
          reason: rejectComment.trim() || rejectReason,
        });
      } else if (platform === 'Wolt') {
        // Wolt reject takes a single free-form reason string.
        await ExternalOrderService.woltReject(activeAlert.id, {
          reason: rejectComment.trim() || rejectReason,
        });
      } else {
        await ExternalOrderService.boltReject(activeAlert.id, {
          rejectReason,
          rejectComment: rejectComment.trim() || undefined,
        });
      }
      snackbar.open('Order declined');
      closeAll();
    } catch (e) {
      snackbar.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const closeAll = () => {
    setActiveAlert(null);
    setAcceptOpen(false);
    setDeclineOpen(false);
    setRejectComment('');
    setRejectReason('too_many_orders');
    setPrepMinutes(25);
    staffItemNotesRef.current = {};
  };

  const parsed = (activeAlert?.parsedPayload as BoltParsedPayload) || undefined;

  return (
    <React.Fragment>
      {/* Audio element always mounted — start/stop driven by state. */}
      <audio ref={audioRef} src="/sounds/order-alert.wav" loop />

      {/* Persistent top-right button so alert-eligible staff can unlock
          audio autoplay BEFORE the first order fires. Disappears once
          audio is enabled. Hidden for users who get no alerts. */}
      {audioBlocked &&
        ((allowedShopIds && allowedShopIds.length > 0) ||
          websiteShopIds.length > 0) && (
          <button
            type="button"
            className={classes.enableSoundBanner}
            onClick={enableAudioManually}
          >
            <VolumeUpIcon fontSize="small" />
            Enable order alert sound
          </button>
        )}

      {/* Main takeover dialog — uncloseable except via Accept/Decline. */}
      <Dialog
        open={!!activeAlert && !suppressed}
        fullWidth
        maxWidth="sm"
        disableBackdropClick
        disableEscapeKeyDown
        PaperProps={{ className: classes.dialog }}
      >
        <div className={classes.alertHeader} style={{ background: accent }}>
          <VolumeUpIcon className={classes.pulse} fontSize="large" />
          <span style={{ flex: 1 }}>
            {isGloria ? 'GloriaFood — review comment' : `New ${platform} order`}
          </span>
          {audioBlocked && (
            <Button
              variant="contained"
              size="small"
              onClick={enableAudioManually}
              style={{
                background: 'white',
                color: accent,
                fontWeight: 700,
              }}
            >
              🔔 Enable sound
            </Button>
          )}
        </div>

        <DialogContent style={{ paddingTop: 16 }}>
          {parsed && (
            <React.Fragment>
              <Typography className={classes.customerLine}>
                {parsed.customer?.partialName || '— no name —'} ·{' '}
                <span style={{ fontWeight: 400 }}>
                  #{parsed.orderReference || '—'}
                </span>
              </Typography>

              <div className={classes.metaChips}>
                {parsed.orderType === 'takeaway' ? (
                  <Chip
                    label="PICKUP"
                    size="small"
                    className={classes.chipPickup}
                  />
                ) : parsed.orderType === 'delivery' ? (
                  <Chip
                    label={
                      // Website orders: WE deliver, so show the town — prep
                      // time and driver planning depend on it.
                      isWebsite && parsed.customer?.addressInfo?.city
                        ? `DELIVERY · ${parsed.customer.addressInfo.city}`
                        : 'DELIVERY'
                    }
                    size="small"
                    className={classes.chipDelivery}
                  />
                ) : null}
                {isWebsite ? (
                  // Money flag: cash must be COLLECTED on hand-over, card is
                  // already settled (card orders only reach staff once the
                  // payment cleared).
                  (parsed.paymentType || '').toLowerCase() === 'card' ? (
                    <Chip
                      label="PAID"
                      size="small"
                      style={{ background: '#2e7d32', color: 'white', fontWeight: 700 }}
                    />
                  ) : (
                    <Chip
                      label="CASH"
                      size="small"
                      style={{ background: '#c62828', color: 'white', fontWeight: 700 }}
                    />
                  )
                ) : null}
                {(() => {
                  if (!parsed.dueDatetime) return null;
                  const due = new Date(parsed.dueDatetime);
                  if (isNaN(due.getTime())) return null;
                  const min = (due.getTime() - Date.now()) / 60000;
                  if (min > 30) {
                    // Show the actual due time, not just the word — staff
                    // need to know WHEN before choosing a prep time. The
                    // kitchen start is derived the same way the API does
                    // it (prep + courier buffer before due).
                    const hhmm = (d: Date) =>
                      `${d.getHours().toString().padStart(2, '0')}:${d
                        .getMinutes()
                        .toString()
                        .padStart(2, '0')}`;
                    const kitchenStart = new Date(
                      due.getTime() - (prepMinutes + 10) * 60000,
                    );
                    return (
                      <React.Fragment>
                        <Chip
                          label={`SCHEDULED · DUE ${hhmm(due)}`}
                          size="small"
                          className={classes.chipScheduled}
                        />
                        {/* Bolt/Wolt only: their kitchen lead is prep+10.
                            GloriaFood/Website pre-orders schedule on the
                            global delivery-time preference instead, so this
                            estimate would be wrong for them. */}
                        {!isGloria && !isWebsite ? (
                          <Chip
                            label={`kitchen starts ~${hhmm(kitchenStart)}`}
                            size="small"
                            variant="outlined"
                          />
                        ) : null}
                      </React.Fragment>
                    );
                  }
                  return null;
                })()}
              </div>

              {parsed.orderNote ? (
                <div
                  style={{
                    background: '#fff8e1',
                    border: '1px solid #ffe082',
                    borderRadius: 4,
                    padding: '8px 12px',
                    margin: '8px 0',
                    fontWeight: 700,
                    color: '#795548',
                  }}
                >
                  ✎ Customer note: {parsed.orderNote}
                  {isGloria ? (
                    <div
                      style={{ fontWeight: 400, fontSize: 12, marginTop: 4 }}
                    >
                      Add it to the dishes it applies to below, then push to the
                      kitchen.
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className={classes.itemList}>
                {(parsed.items || []).map((item, i) => (
                  <div key={i}>
                    <div className={classes.itemRow}>
                      <span className={classes.itemName}>
                        {item.qty}× {item.name || '(no name)'}
                      </span>
                      <span>
                        {formatMoney(item.totalPrice, parsed.totalCurrency)}
                      </span>
                    </div>
                    {(item.options || []).map((opt, oi) => (
                      <div key={oi} className={classes.optionRow}>
                        + {opt.qty}× {opt.name || '(no name)'}
                      </div>
                    ))}
                    <TextField
                      key={`note-${activeAlert?.id}-${i}`}
                      placeholder="Add note for kitchen (optional)"
                      size="small"
                      variant="outlined"
                      fullWidth
                      defaultValue=""
                      onChange={(e) => {
                        staffItemNotesRef.current[i] = e.target.value;
                      }}
                      style={{ margin: '4px 0 10px' }}
                    />
                  </div>
                ))}
                <div className={classes.total}>
                  <span>Total</span>
                  <span>
                    {formatMoney(parsed.totalPrice, parsed.totalCurrency)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          )}
        </DialogContent>

        <DialogActions className={classes.actions}>
          {/* GloriaFood is already accepted on their side — no decline. */}
          {!isGloria ? (
            <Button
              className={classes.declineBtn}
              startIcon={<CancelIcon />}
              disabled={submitting}
              onClick={() => setDeclineOpen(true)}
            >
              Decline
            </Button>
          ) : null}
          <Button
            className={classes.acceptBtn}
            style={{ background: accent }}
            startIcon={
              submitting && (acceptOpen || isGloria) ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CheckCircleIcon />
              )
            }
            disabled={submitting}
            onClick={isGloria ? handleAccept : () => setAcceptOpen(true)}
          >
            {isGloria ? 'Push to kitchen' : 'Accept'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Accept sub-dialog: pick prep minutes, then confirm. */}
      <Dialog
        open={acceptOpen}
        onClose={() => !submitting && setAcceptOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Accept order — prep time?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            How many minutes until the order will be ready?
            {isWebsite
              ? ' The customer is watching their status page and will see this as their waiting time.'
              : ''}
          </Typography>
          <TextField
            autoFocus
            type="number"
            fullWidth
            variant="outlined"
            label="Prep minutes"
            value={prepMinutes}
            onChange={(e) => setPrepMinutes(parseInt(e.target.value || '0', 10))}
            inputProps={{ min: 5, max: 120, step: 5 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAccept}
            disabled={submitting || prepMinutes < 5}
          >
            {submitting ? 'Accepting…' : 'Confirm Accept'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decline sub-dialog: pick reason, optional comment. */}
      <Dialog
        open={declineOpen}
        onClose={() => !submitting && setDeclineOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Decline order — why?</DialogTitle>
        <DialogContent>
          <FormControl fullWidth variant="outlined" style={{ marginBottom: 16 }}>
            <InputLabel>Reason</InputLabel>
            <Select
              label="Reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value as any)}
            >
              {REJECT_REASONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Comment (optional)"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleDecline}
            disabled={submitting}
          >
            {submitting ? 'Declining…' : 'Confirm Decline'}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};
